import get from 'lodash/get';
import { isBase64 } from 'utils/checkImage';
import { getValueOfObject } from 'utils/details';
import { DATE_TIME_FORMAT, convertTime } from 'utils/time';
import { audienceList } from '../../../../mocks/utils';
import { MEDIA_TYPE_API_ENUMS } from '../../../constants/media';
import { v4 } from 'uuid';
import isEmpty from 'lodash/isEmpty';

// This is required because for recommendations, backend needs audience list ids as INTERNAL_AUDIENCE, CLIENT_AUDIENCE and PUBLIC_AUDIENCE
// and we need to display it as Internal Audience, Client Audience and Public Audience

const transformRecommendationsForApi = vals => {
  return vals.map(recommendation => {
    return {
      ...recommendation,
      audience:
        audienceList.filter(f => f.id === recommendation.audience).length === 0
          ? audienceList.filter(f => f.id === recommendation.audience)[0].id
          : recommendation.audience,
    };
  });
};

const getLocationDetails = locationArray => {
  return locationArray.map(location => {
    if (location.paths) {
      return {
        polygon: {
          points: location.paths.map(path => ({
            longitude: path.lng,
            latitude: path.lat,
          })),
        },
      };
    } else if (location.center) {
      return {
        ellipse: {
          centre: {
            latitude: location.center.lat,
            longitude: location.center.lng,
            isWaypoint: location.center.isWaypoint,
            radius: location.center.radius,
          },
          radiusX: location.radiusX,
          radiusY: location.radiusY,
          angle: location.rotationAngle,
        },
      };
    } else {
      return {
        point: { longitude: location.lng, latitude: location.lat },
      };
    }
  });
};

export const transformEventObject = data => {
  const img = `https://storage.googleapis.com/events_images-production/placeholder_image.png?${v4()}`;
  const aud = `https://storage.googleapis.com/events_images-production/placeholder_audio.png?${v4()}`;
  return {
    general: {
      title: get(data, 'title'),
      descriptions: {
        publicDescription: get(data, 'publicDescription'),
        internalDescription: get(data, 'internalDescription'),
        whatHappened: get(data, 'whatHappened'),
        whereHappened: get(data, 'whereHappened'),
        whoHappened: get(data, 'whoHappened'),
        whenHappened: get(data, 'whenHappened'),
        whyHappened: get(data, 'whyHappened'),
        howHappened: get(data, 'howHappened'),
      },
      creator: get(data, 'creator'),
      credibility: get(data, 'credibility'),
      eventId: get(data, 'eventId'),
      regionId: get(data, 'regionId'),
      securityCompanyId: get(data, 'securityCompanyId'),
      securityCompanyName: get(data, 'securityCompanyName'),
      eventStartTime: convertTime(get(data, 'startTime'), DATE_TIME_FORMAT),
      eventFinishTime: data.finishTime
        ? convertTime(get(data, 'finishTime'), DATE_TIME_FORMAT)
        : null,
      category: get(data, 'eventCategory'),
      type: get(data, 'eventType').typeName,
      severity: get(data, 'severity'),
      severityRating: get(data, 'severityRating'),
      ...(data?.archived && { archived: data?.archived }),
    },
    details: {
      ...(!isEmpty(data?.site) && { site: data.site }),
      involvedCompanies:
        data.involvedCompanies && getValueOfObject(data, 'involvedCompanies'),
      involvedAssets:
        data.involvedAssets &&
        getValueOfObject(data, 'involvedAssets').map(asset => ({
          id: asset.id,
          assetId: asset.assetId,
          name: asset.assetName,
          type: {
            id: asset.typeId,
            category: asset.category,
            name: asset.originalTypeName,
          },
        })),
      involvedPeople:
        data.involvedPeople && getValueOfObject(data, 'involvedPeople'),
      sources: {
        reporters: data.reporters && getValueOfObject(data, 'reporters'),
        websites: data.websites && getValueOfObject(data, 'websites'),
      },
      recommendations:
        data.recommendations &&
        transformRecommendationsForApi(
          getValueOfObject(data, 'recommendations'),
        ),
      media: data.media
        ? get(data, 'media').reduce((acc, currentVal, index) => {
            if (isBase64(currentVal.url)) {
              const removeBase64 = {
                mediaType: currentVal.mediaType,
                name: currentVal.name,
                description: currentVal.description,
                url:
                  currentVal.mediaType === MEDIA_TYPE_API_ENUMS.IMAGE
                    ? `${img}-${index}`
                    : `${aud}-${index}`,
              };
              return [...acc, removeBase64];
            } else {
              const withoutId = {
                mediaType: currentVal.mediaType,
                name: currentVal.name,
                description: currentVal.description,
                url: currentVal.url,
                isThumbnail: currentVal.isThumbnail,
              };
              return [...acc, withoutId];
            }
          }, [])
        : [],
    },
    locations: data.locations && getLocationDetails(get(data, 'locations')),
    publishing: data?.managerialOnlyDistribution
      ? { distribution: 'MANAGER' }
      : get(data, 'publishing'),
    media:
      data?.media && data.media.length > 0
        ? get(data, 'media').reduce((acc, currentVal, index) => {
            if (isBase64(currentVal.url)) {
              const addBase64 = {
                mediaType:
                  currentVal.mediaType === MEDIA_TYPE_API_ENUMS.AUDIO
                    ? MEDIA_TYPE_API_ENUMS.AUDIO
                    : MEDIA_TYPE_API_ENUMS.IMAGE,
                name: currentVal.name,
                description: currentVal.description,
                base64: currentVal.url,
                url:
                  currentVal.mediaType === MEDIA_TYPE_API_ENUMS.IMAGE
                    ? `${img}-${index}`
                    : `${aud}-${index}`,
              };
              return [...acc, addBase64];
            } else {
              return acc;
            }
          }, [])
        : [],
  };
};
